import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/logger";
import { Vote, Loader2, ChevronRight } from "lucide-react";

export default function MyVotes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [votes, setVotes] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/me/votes");
        setVotes(data);
      } catch (err) {
        logError("Failed to load vote history:", err);
        setVotes([]);
      }
    })();
  }, [user, navigate]);

  return (
    <div className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-10 flex items-center gap-3 border-b border-[#333] pb-6">
        <Vote className="h-6 w-6 text-[#3B82F6]" strokeWidth={1.5} />
        <div>
          <h1 className="font-heading text-3xl tracking-tight text-white">Your Votes</h1>
          <div className="meta-label mt-1 text-[#737373]">Where you've weighed in</div>
        </div>
      </div>

      {votes === null ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-[#3B82F6]" />
        </div>
      ) : votes.length === 0 ? (
        <div className="py-24 text-center">
          <p className="font-heading text-2xl text-white">No votes yet.</p>
          <p className="meta-label mt-3 text-[#737373]">Open a dossier and cast your read to build your record.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-votes-list">
          {votes.map((v) => {
            const a = v.votes?.a || 0;
            const b = v.votes?.b || 0;
            const total = a + b;
            const pctA = total ? Math.round((a / total) * 100) : 0;
            const accent = v.side === "a" ? "#D92D20" : "#3B82F6";
            return (
              <Link
                key={v.topic_id}
                to={`/topic/${v.topic_id}`}
                data-testid={`my-vote-${v.topic_id}`}
                className="panel group flex items-center justify-between p-4 transition-colors hover:border-[#3B82F6]/50"
              >
                <div className="min-w-0">
                  <div className="truncate font-body text-white">{v.title}</div>
                  <div className="meta-label mt-1 flex items-center gap-2 text-[#737373]">
                    <span>{v.category}</span>
                    <span style={{ color: accent }}>
                      You picked: {v.side_label}
                    </span>
                  </div>
                  <div className="mt-2 flex h-1 w-48 max-w-full overflow-hidden">
                    <div className="h-1 bg-[#D92D20]" style={{ width: `${pctA}%` }} />
                    <div className="h-1 bg-[#3B82F6]" style={{ width: `${100 - pctA}%` }} />
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-[#737373] transition-colors group-hover:text-[#3B82F6]" strokeWidth={1.5} />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
